export interface EclipseProperties {
  id: number;
  size: number;
  x: number;
  y: number;
  rotation: number;
  opacity: number;
}

export class EclipseRenderer {
  private static readonly MinSize: number = 80;
  private static readonly MaxSize: number = 150;
  private static readonly MinOpacity: number = 0.05;
  private static readonly MaxOpacity: number = 0.3;
  private static readonly GridSize: number = 5; // 5x5 grid

  private x: number = 0;
  private y: number = 0;
  private size: number = 0;
  private rotation: number = 0;

  public constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.setProperties();
  }

  private setProperties(): void {
    const { MinSize, MaxSize } = EclipseRenderer;

    this.size = Math.floor(Math.random() * (MaxSize - MinSize + 1)) + MinSize;

    this.rotation = Math.random() * 360;
  }

  public getProperties() {
    return {
      x: this.x,
      y: this.y,
      size: this.size,
      rotation: this.rotation,
    };
  }

  private static getRandomPositionInGridCell(
    gridX: number,
    gridY: number
  ): { x: number; y: number } {
    const cellWidth = 100 / EclipseRenderer.GridSize;
    const cellHeight = 100 / EclipseRenderer.GridSize;

    // Add some padding to prevent eclipses from being too close to the edges
    const padding = 5;

    return {
      x: gridX * cellWidth + padding + Math.random() * (cellWidth - 2 * padding),
      y: gridY * cellHeight + padding + Math.random() * (cellHeight - 2 * padding),
    };
  }

  public static generateEclipse(id: number): EclipseProperties {
    const { MinSize, MaxSize, MinOpacity, MaxOpacity, GridSize } = EclipseRenderer;

    // Calculate grid position based on ID
    const gridX = id % GridSize;
    const gridY = Math.floor(id / GridSize) % GridSize;

    // Get random position within the grid cell
    const position = EclipseRenderer.getRandomPositionInGridCell(gridX, gridY);

    return {
      id,
      size: Math.floor(Math.random() * (MaxSize - MinSize + 1)) + MinSize,
      x: position.x,
      y: position.y,
      rotation: Math.random() * 360,
      opacity: Math.random() * (MaxOpacity - MinOpacity) + MinOpacity,
    };
  }

  public static generateEclipses(count: number): EclipseProperties[] {
    // Ensure count doesn't exceed grid capacity
    const maxCount = EclipseRenderer.GridSize * EclipseRenderer.GridSize;
    const actualCount = Math.min(count, maxCount);

    return Array.from({ length: actualCount }, (_, index) =>
      EclipseRenderer.generateEclipse(index)
    );
  }
}
